/**
 * Hub client `/projects/:id` — parcours guide a 6 etapes en mode pack-first.
 *
 * Le funnel BCL est unique et toujours dans le meme ordre : Pack -> Qualif
 * -> Infos -> Fibre -> Documents -> Paiement. Cette page affiche l'etape
 * courante (et seulement celle-ci) plus la timeline de suivi pour les
 * dossiers deja en cours de traitement BCL.
 *
 * Le paiement vaut soumission automatique : plus de section « Valider mon
 * dossier » separee.
 */
import type { Project } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { getStatusColor } from '@/features/dashboard/status-color';
import { extractErrorMessage } from '@/features/auth/error-message';
import { DocumentsSection } from '@/features/documents/documents-section';
import { listDocumentsForProject } from '@/features/documents/documents-api';
import { PaymentSection } from '@/features/payments/payment-section';
import { listPaymentsForProject, syncPaymentFromFam } from '@/features/payments/payments-api';
import {
  chooseAppointmentSlot,
  listMyAppointments,
  rejectAppointmentSlots,
} from '@/features/appointments/appointments-api';
import { formatSlot } from '@/features/appointments/appointment-labels';
import {
  computeBookingSteps,
  nextActionTarget,
  resolveInitialStep,
} from '@/features/projects/booking-progress';
import { BookingStepper } from '@/features/projects/booking-stepper';
import {
  packLabels,
  requestTypeLabels,
  statusLabels,
} from '@/features/projects/labels';
import { getProject } from '@/features/projects/projects-api';
import { Timeline } from '@/features/timeline/timeline';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ?? '';
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!projectId) return;
    getProject(projectId)
      .then(setProject)
      .catch((e) => setError(extractErrorMessage(e)));
  }, [projectId]);

  const qc = useQueryClient();

  // Au retour du portail FAM avec ?paymentSuccess=1, on interroge FAM via le
  // back pour synchroniser l'etat du paiement (?webhook peut etre en retard
  // ou avoir un RessourceId different du sessionId stocke en famReference).
  useEffect(() => {
    if (searchParams.get('paymentSuccess') === '1' && projectId) {
      (async () => {
        try {
          const pays = await qc.fetchQuery({
            queryKey: ['payments', 'by-project', projectId],
            queryFn: () => listPaymentsForProject(projectId),
          });
          const pending = pays.find((p) => p.status === 'en_attente' && p.method === 'carte');
          if (pending) {
            const res = await syncPaymentFromFam(pending.id);
            if (res.updated) {
              toast.success(
                'Paiement reçu. Votre dossier est en attente des prochaines étapes côté BCL.',
              );
              await qc.invalidateQueries({ queryKey: ['payments', 'by-project', projectId] });
              await qc.invalidateQueries({ queryKey: ['projects', 'mine'] });
              getProject(projectId).then(setProject).catch(() => undefined);
            } else {
              toast.info(
                'Paiement en cours de validation côté banque. Le dossier sera mis à jour automatiquement dès réception.',
              );
            }
          }
        } catch {
          // Mode degrade : si la sync echoue, on garde l'UI courante. Le
          // webhook FAM ou l'admin pourra confirmer plus tard.
          toast.info(
            'Paiement bien reçu. Le statut sera mis à jour automatiquement.',
          );
        } finally {
          searchParams.delete('paymentSuccess');
          setSearchParams(searchParams, { replace: true });
        }
      })();
    } else if (searchParams.get('paymentCanceled') === '1') {
      toast.info('Paiement annulé. Vous pouvez le relancer quand vous voulez.');
      searchParams.delete('paymentCanceled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, projectId, qc]);

  const documents = useQuery({
    queryKey: ['documents', 'by-project', projectId],
    queryFn: () => listDocumentsForProject(projectId),
    enabled: Boolean(projectId),
  });
  const payments = useQuery({
    queryKey: ['payments', 'by-project', projectId],
    queryFn: () => listPaymentsForProject(projectId),
    enabled: Boolean(projectId),
  });
  const appointments = useQuery({
    queryKey: ['appointments', 'mine'],
    queryFn: listMyAppointments,
    enabled: Boolean(projectId),
  });
  const rdv =
    (appointments.data ?? []).find((a) => a.projectId === projectId) ?? null;

  const chooseMut = useMutation({
    mutationFn: (slotIndex: number) => chooseAppointmentSlot(rdv!.id, slotIndex),
    onSuccess: () => {
      toast.success('Créneau confirmé');
      qc.invalidateQueries({ queryKey: ['appointments', 'mine'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectAppointmentSlots(rdv!.id),
    onSuccess: () => {
      toast.success('BCL vous proposera d’autres créneaux');
      qc.invalidateQueries({ queryKey: ['appointments', 'mine'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  if (error) {
    return (
      <DashboardLayout title="Dossier">
        <Card>
          <CardContent className="py-8">
            <p role="alert" className="text-destructive">
              {error}
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/dashboard">
                <ArrowLeft className="mr-2 h-4 w-4" /> Retour au dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout title="Dossier">
        <p className="text-sm text-muted-foreground">Chargement du projet…</p>
      </DashboardLayout>
    );
  }

  const fullAddress = project.info
    ? `${project.info.address.line1}, ${project.info.address.postalCode} ${project.info.address.city}`
    : 'Adresse à compléter';

  const documentsCount = documents.data?.length ?? 0;
  const paymentDone = (payments.data ?? []).some((p) => p.status === 'paye');
  const bookingSteps = computeBookingSteps(project, {
    documentsCount,
    paymentDone,
  });
  const cta = nextActionTarget(project, { documentsCount, paymentDone });
  const currentStep = resolveInitialStep(project, {
    documentsCount,
    paymentDone,
  });

  return (
    <DashboardLayout title={`Dossier ${project.reference}`}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="mb-2">
              <Link to="/dashboard/requests">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour aux demandes
              </Link>
            </Button>
            <h2 className="text-lg font-semibold">{project.reference}</h2>
            <p className="text-sm text-gray-500">{fullAddress}</p>
          </div>
          <Badge
            variant="outline"
            className={getStatusColor(statusLabels[project.status])}
          >
            {statusLabels[project.status]}
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Suivi de votre dossier</CardTitle>
            <CardDescription>
              {requestTypeLabels[project.requestType]} — funnel en 6 étapes,
              toujours dans le même ordre.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <BookingStepper steps={bookingSteps} />
            {cta && (
              <div className="flex flex-col gap-3 rounded-md border border-bcl-blue/30 bg-bcl-blue/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm">
                  <span className="font-medium">Prochaine étape :</span>{' '}
                  {cta.label}
                </p>
                {cta.href.startsWith('#') ? (
                  <Button asChild>
                    <a href={cta.href}>
                      Poursuivre mon dossier
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                ) : (
                  <Button asChild>
                    <Link to={cta.href}>
                      Poursuivre mon dossier
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            )}
            {project.packCode && (
              <p className="text-sm">
                <span className="text-muted-foreground">Pack choisi : </span>
                <strong>
                  {packLabels[project.packCode] ?? project.packCode}
                </strong>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Action requise : le client doit choisir son créneau de rendez-vous. */}
        {rdv?.status === 'creneaux_proposes' && (
          <Card className="border-2 border-amber-400 bg-amber-50 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                </span>
                Action requise : choisissez votre créneau
              </CardTitle>
              <CardDescription>
                BCL vous propose ces créneaux pour votre rendez-vous.
                Sélectionnez celui qui vous convient.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {rdv.proposedSlots.map((slot, i) => (
                  <Button
                    key={slot}
                    disabled={chooseMut.isPending}
                    onClick={() => chooseMut.mutate(i)}
                  >
                    {formatSlot(slot)}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                disabled={rejectMut.isPending}
                onClick={() => rejectMut.mutate()}
              >
                Aucune date ne me convient
              </Button>
            </CardContent>
          </Card>
        )}

        {rdv?.status === 'confirme' && rdv.confirmedSlot && (
          <Card className="border-emerald-300 bg-emerald-50">
            <CardHeader>
              <CardTitle>Rendez-vous confirmé</CardTitle>
              <CardDescription>
                Votre rendez-vous est prévu le{' '}
                <strong>{formatSlot(rdv.confirmedSlot)}</strong>. BCL vous
                affecte un technicien.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Etape Pack / Qualif / Info / Fiber : on renvoie vers le wizard */}
        {['pack', 'qualification', 'info', 'fiber'].includes(currentStep) && (
          <Card>
            <CardHeader>
              <CardTitle>Reprendre le funnel</CardTitle>
              <CardDescription>
                Continuez votre dossier dans le parcours guidé.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to={`/projects/${project.id}/wizard`}>
                  Continuer
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Etape Documents : on affiche la section avec la checklist */}
        {currentStep === 'documents' && (
          <section id="documents">
            <DocumentsSection
              projectId={project.id}
              requestType={project.requestType}
            />
          </section>
        )}

        {/* Etape Paiement : on affiche la section paiement */}
        {currentStep === 'payment' && (
          <section id="paiement">
            <PaymentSection
              project={project}
              documentsCount={documentsCount}
            />
          </section>
        )}

        {/* Dossier complet : on affiche docs + paiement pour archive */}
        {currentStep === 'done' && (
          <>
            <section id="documents">
              <DocumentsSection
                projectId={project.id}
                requestType={project.requestType}
              />
            </section>
            <section id="paiement">
              <PaymentSection
                project={project}
                documentsCount={documentsCount}
              />
            </section>
          </>
        )}

        <Timeline projectId={project.id} />
      </div>
    </DashboardLayout>
  );
}
